// Message Item Component
// Following existing patterns from ActivityFeed

import React, { useState } from 'react';
import { MessageItemProps } from '../../../types/messaging';
import { DeleteMessage } from '../MessageActions/DeleteMessage';
import { EditMessage } from '../MessageActions/EditMessage';
import { ReplyToMessage } from '../MessageActions/ReplyToMessage';
import { ForwardMessage } from '../MessageActions/ForwardMessage';
import { TextMessage } from '../MessageTypes/TextMessage';
import { ImageMessage } from '../MessageTypes/ImageMessage';
import { FileMessage } from '../MessageTypes/FileMessage';
import { AudioMessage } from '../MessageTypes/AudioMessage';
import {
  MessageItem as StyledMessageItem,
  MessageBubble,
  MessageContent,
  MessageTime,
  MessageStatus,
  StatusIcon,
  MessageActions,
  MessageActionButton
} from '../../../styles/Messaging';

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwn,
  onAction,
  conversations = []
}) => {
  const [showActions, setShowActions] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (message: any) => {
    if (!message.isDelivered) return '⏳';
    if (!message.isRead) return '✓';
    return '✓✓';
  };

  const handleAction = (action: string) => {
    setActiveAction(action);
    setShowActions(false);
  };

  /**
   * Actions raised from inside a message body (TextMessage's icon row).
   *
   * Edit and unsend open the confirmation/editor this component already owns;
   * anything else goes straight up. Passing `onAction` down unchanged meant
   * TextMessage's edit fired the parent's handler with no new content, so the
   * pencil resolved to nothing at all.
   */
  const handleBodyAction = async (messageId: string, action: string, data?: any) => {
    if (action === 'edit') return handleAction('edit');
    // Unsend removes the message for the other person too, so it asks first.
    if (action === 'unsend') return handleAction('unsend');
    // Hiding only affects this viewer's own thread — no confirmation needed
    // for something nobody else can see the effect of.
    return onAction(messageId, action, data);
  };

  const handleActionComplete = async (action: string, data?: any) => {
    try {
      await onAction(message.id, action, data);
      setActiveAction(null);
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  const handleActionCancel = () => {
    setActiveAction(null);
  };

  return (
    <StyledMessageItem 
      $isOwn={isOwn}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Render appropriate message type component */}
      {message.messageType === 'text' && (
        <TextMessage
          message={message}
          isOwn={isOwn}
          onAction={handleBodyAction}
        />
      )}
      
      {message.messageType === 'image' && (
        <ImageMessage
          message={message}
          isOwn={isOwn}
          onAction={handleBodyAction}
        />
      )}
      
      {message.messageType === 'file' && (
        <FileMessage
          message={message}
          isOwn={isOwn}
          onAction={handleBodyAction}
        />
      )}
      
      {message.messageType === 'audio' && (
        <AudioMessage
          message={message}
          isOwn={isOwn}
          onAction={handleBodyAction}
        />
      )}
      
      {/* Fallback for unknown message types */}
      {!['text', 'image', 'file', 'audio'].includes(message.messageType) && (
        <MessageBubble $isOwn={isOwn}>
          <MessageContent>{message.content}</MessageContent>
          <MessageTime $isOwn={isOwn}>
            {formatTime(message.createdAt)}
          </MessageTime>
          {isOwn && (
            <MessageStatus $isOwn={isOwn}>
              <StatusIcon>{getStatusIcon(message)}</StatusIcon>
            </MessageStatus>
          )}
          
          <MessageActions $isVisible={showActions}>
              <MessageActionButton
                onClick={() => handleAction('reply')}
                title="Reply"
              >
                ↩️
              </MessageActionButton>
              <MessageActionButton
                onClick={() => handleAction('forward')}
                title="Forward"
              >
                ↪️
              </MessageActionButton>
              {isOwn && (
                <MessageActionButton
                  onClick={() => handleAction('edit')}
                  title="Edit"
                >
                  ✏️
                </MessageActionButton>
              )}
              <MessageActionButton
                onClick={() => handleAction('delete')}
                title="Delete"
              >
                🗑️
              </MessageActionButton>
          </MessageActions>
        </MessageBubble>
      )}
      
      {/* Action Components */}
      {(activeAction === 'delete' || activeAction === 'unsend') && (
        <DeleteMessage
          message={{
            id: message.id,
            content: message.content,
            senderName: message.senderId === 'current-user' ? 'You' : 'User',
            createdAt: message.createdAt
          }}
          onDeleteMessage={async () => {
            // 'unsend' and the older 'delete' both mean "remove for everyone";
            // the action name is kept so the parent can tell which entry point
            // was used.
            await handleActionComplete(activeAction === 'unsend' ? 'unsend' : 'delete');
          }}
          onCancel={handleActionCancel}
        />
      )}
      
      {activeAction === 'edit' && isOwn && (
        <EditMessage
          message={{
            id: message.id,
            content: message.content
          }}
          onSaveEdit={async (messageId, newContent) => {
            await handleActionComplete('edit', { newContent });
          }}
          onCancel={handleActionCancel}
        />
      )}
      
      {activeAction === 'reply' && (
        <ReplyToMessage
          originalMessage={{
            id: message.id,
            content: message.content,
            senderName: message.senderId === 'current-user' ? 'You' : 'User',
            createdAt: message.createdAt
          }}
          onSendReply={async (replyText, originalMessageId) => {
            await handleActionComplete('reply', { replyText, originalMessageId });
          }}
          onCancel={handleActionCancel}
        />
      )}
      
      {activeAction === 'forward' && (
        <ForwardMessage
          message={{
            id: message.id,
            content: message.content,
            senderName: message.senderId === 'current-user' ? 'You' : 'User',
            createdAt: message.createdAt
          }}
          conversations={conversations}
          onForwardMessage={async (conversationId, messageId, comment) => {
            await handleActionComplete('forward', { conversationId, comment });
          }}
          onCancel={handleActionCancel}
        />
      )}
    </StyledMessageItem>
  );
};
